import React from "react";
import { Badge } from "../ui/Badge";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  TrophyIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";

const getOrdinalSuffix = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const formatRanking = (rank, total, percentile) => {
  if (rank === null || total === null) return "N/A";
  const suffix = getOrdinalSuffix(rank);
  return `${rank}${suffix} of ${total} (Top ${Math.round(100 - percentile)}%)`;
};

const getScoreColor = (score, maxScore = 10) => {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 80) return "text-green-600";
  if (percentage >= 60) return "text-yellow-600";
  return "text-red-600";
};

const getPercentileColor = (percentile) => {
  if (percentile >= 75) return "bg-green-500";
  if (percentile >= 50) return "bg-yellow-500";
  if (percentile >= 25) return "bg-orange-500";
  return "bg-red-500";
};

const DEFAULT_PRIMARY_COLOR = "#0F172A";
const DEFAULT_SECONDARY_COLOR = "#0EA5E9";

const ParentReportContent = ({
  reportData,
  expandedCategories,
  onToggleCategory,
}) => {
  const { player, evaluation, overall, categories, evaluator_notes } =
    reportData;
  const primaryColor = evaluation.primary_color || DEFAULT_PRIMARY_COLOR;
  const secondaryColor = evaluation.secondary_color || DEFAULT_SECONDARY_COLOR;

  return (
    <div id="parent-report-content" className="space-y-6 bg-white p-4">
      {/* Header */}
      <div className="border-b-4 pb-4" style={{ borderColor: primaryColor }}>
        <div className="flex items-start justify-between">
          <div>
            {evaluation.club_logo_url && (
              <img
                src={evaluation.club_logo_url}
                alt={evaluation.club_name}
                className="h-12 w-auto mb-2"
              />
            )}
            <h2 className="text-xl font-bold text-gray-900">
              {evaluation.club_name}
            </h2>
            <p className="text-gray-600">{evaluation.name}</p>
            {evaluation.report_header_text && (
              <p className="mt-2 text-sm font-medium" style={{ color: primaryColor }}>
                {evaluation.report_header_text}
              </p>
            )}
            {evaluation.season && (
              <p className="text-sm text-gray-500">
                {evaluation.season} {evaluation.year}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              Generated:{" "}
              {new Date(reportData.generated_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Player Info */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{player.name}</h3>
            <div className="flex items-center space-x-4 mt-1 text-gray-600">
              {player.number && <span>#{player.number}</span>}
              {player.position && <span>{player.position}</span>}
              {player.grade && <span>Grade {player.grade}</span>}
              {player.group && <Badge variant="blue">{player.group}</Badge>}
            </div>
          </div>
          {overall && (
            <div className="text-right">
              <p className="text-sm text-gray-600">Overall Score</p>
              <p
                className={`text-3xl font-bold ${getScoreColor(overall.average_score)}`}
                style={{ color: primaryColor }}
              >
                {overall.average_score.toFixed(2)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Rankings Section */}
      {overall && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Session Ranking */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <TrophyIcon className="w-5 h-5 text-yellow-500 mr-2" />
              <h4 className="font-semibold text-gray-900">
                Evaluation Ranking
              </h4>
            </div>
            <p className="text-lg font-medium text-gray-800">
              {formatRanking(
                overall.session_rank,
                overall.session_total_players,
                overall.session_percentile,
              )}
            </p>
            <div className="mt-2">
              <div className="flex items-center">
                <span className="text-sm text-gray-500 w-20">Percentile</span>
                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getPercentileColor(overall.session_percentile)}`}
                    style={{
                      width: `${overall.session_percentile}%`,
                      backgroundColor: secondaryColor,
                    }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-700 ml-2 w-12 text-right">
                  {overall.session_percentile.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>

          {/* Group Ranking */}
          {overall.group_rank && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <UserGroupIcon className="w-5 h-5 text-slate-500 mr-2" />
                <h4 className="font-semibold text-gray-900">
                  Group Ranking ({player.group})
                </h4>
              </div>
              <p className="text-lg font-medium text-gray-800">
                {formatRanking(
                  overall.group_rank,
                  overall.group_total_players,
                  overall.group_percentile,
                )}
              </p>
              <div className="mt-2">
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 w-20">Percentile</span>
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getPercentileColor(overall.group_percentile)}`}
                      style={{
                        width: `${overall.group_percentile}%`,
                        backgroundColor: secondaryColor,
                      }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700 ml-2 w-12 text-right">
                    {overall.group_percentile.toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Breakdown */}
      <div className="space-y-3">
        <h4 className="font-semibold text-gray-900 flex items-center">
          <ClipboardDocumentListIcon className="w-5 h-5 mr-2" />
          Skills Breakdown by Category
        </h4>

        {categories.map((category) => {
          const isExpanded = expandedCategories.has(category.id);

          return (
            <div
              key={category.id}
              className="border border-gray-200 rounded-lg overflow-hidden"
              style={{ borderColor: secondaryColor }}
            >
              <button
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 hover:bg-gray-100 print:bg-gray-50"
                onClick={() => onToggleCategory(category.id)}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <span className="font-medium text-gray-900">
                    {category.name}
                  </span>
                  <Badge variant="blue">
                    Avg: {category.average_score.toFixed(2)}
                  </Badge>
                  {category.session_rank && (
                    <span className="text-sm text-gray-500">
                      Rank: {category.session_rank}/
                      {category.session_total_players}
                    </span>
                  )}
                </div>
                <div className="flex items-center print:hidden">
                  {isExpanded ? (
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {(isExpanded || true) && ( // Always show in print
                <div
                  className={`px-4 py-3 ${isExpanded ? "" : "hidden print:block"}`}
                >
                  <div className="grid gap-2">
                    {category.skills.map((skill) => (
                      <div
                        key={skill.id}
                        className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">
                            {skill.name}
                          </p>
                          {skill.description && (
                            <p className="text-sm text-gray-500">
                              {skill.description}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          {skill.numeric_score !== null ? (
                            <span
                              className={`text-lg font-semibold ${getScoreColor(skill.numeric_score)}`}
                            >
                              {skill.numeric_score.toFixed(1)}
                            </span>
                          ) : skill.pass_fail_score !== null ? (
                            <Badge
                              variant={skill.pass_fail_score ? "green" : "red"}
                            >
                              {skill.pass_fail_score ? "Pass" : "Fail"}
                            </Badge>
                          ) : skill.text_score ? (
                            <Badge variant="gray">{skill.text_score}</Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                          {skill.evaluator_count > 1 && (
                            <p className="text-xs text-gray-400">
                              ({skill.evaluator_count} evaluators)
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Evaluator Notes */}
      {evaluator_notes && evaluator_notes.length > 0 && (
        <div className="bg-yellow-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Evaluator Notes</h4>
          <ul className="space-y-2">
            {evaluator_notes.map((note, index) => (
              <li
                key={index}
                className="text-gray-700 text-sm pl-4 border-l-2 border-yellow-400"
              >
                {note}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-200 pt-4 text-center text-sm text-gray-500">
        {evaluation.report_footer_text && (
          <p className="mb-2 font-medium" style={{ color: primaryColor }}>
            {evaluation.report_footer_text}
          </p>
        )}
        <p>
          This report contains confidential evaluation data for {player.name}{" "}
          only.
        </p>
        <p>
          Rankings compare against other participants without revealing their
          identities.
        </p>
      </div>
    </div>
  );
};

export { ParentReportContent };
